/* turtees free gift: keeps one free Mummy Bag in the cart while the other items total
   at least the threshold, and takes it out again when they don't. The price itself is
   zeroed by the "Free Mummy Bag on orders over AED 499" automatic discount; this file
   only manages whether the bag line is in the cart. Config comes from layout/theme.liquid. */
import { StandardEvents, CartLinesUpdateEvent } from '@shopify/events';

const config = window.ttFreeGift;

if (config?.variantId) {
  const variantId = Number(config.variantId);
  const threshold = Number(config.threshold); // shop currency, in cents
  const root = window.Shopify?.routes?.root || '/';
  const BLOCK_KEY = 'tt-free-gift-blocked';

  let running = false;
  let queued = false;
  let dispatching = false;

  const isGift = (item) => item.variant_id === variantId;
  const rate = () => Number(window.Shopify?.currency?.rate) || 1;

  const storage = {
    get: () => {
      try { return sessionStorage.getItem(BLOCK_KEY); } catch { return null; }
    },
    set: (value) => {
      try { value === null ? sessionStorage.removeItem(BLOCK_KEY) : sessionStorage.setItem(BLOCK_KEY, value); } catch {}
    },
  };

  const post = (path, body) =>
    fetch(`${root}${path}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    }).then((response) => {
      if (!response.ok) throw new Error(`${path} ${response.status}`);
      return response.json();
    });

  const getCart = () => fetch(`${root}cart.js`, { headers: { Accept: 'application/json' } }).then((r) => r.json());

  // Tell the theme the cart changed so the drawer, cart page and bubble re-render.
  function announce(action, lines, cart) {
    try {
      dispatching = true;
      document.dispatchEvent(
        new CartLinesUpdateEvent({
          action,
          context: 'cart',
          lines,
          promise: Promise.resolve({
            cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
            detail: { itemCount: cart.item_count, source: 'tt-free-gift', didError: false },
          }),
        })
      );
    } catch (error) {
      console.warn('[tt-free-gift] could not refresh cart UI', error);
      if (/\/cart\/?$/.test(window.location.pathname)) window.location.reload();
    } finally {
      dispatching = false;
    }
  }

  async function sync() {
    if (running) {
      queued = true;
      return;
    }
    running = true;

    try {
      let cart = await getCart();
      const gifts = cart.items.filter(isGift);
      const qualifying = cart.items.filter((item) => !isGift(item)).reduce((sum, item) => sum + item.original_line_price, 0);
      const eligible = qualifying >= Math.round(threshold * rate());

      // If the discount refused to make the bag free for this exact cart (for example a code
      // that doesn't combine), don't keep re-adding it until the cart or its codes change.
      const codes = (cart.discount_codes || []).filter((d) => d.applicable).map((d) => d.code).sort().join(',');
      const signature = `${qualifying}|${codes}`;
      const blocked = storage.get();
      if (blocked !== null && blocked !== signature) storage.set(null);
      const isBlocked = storage.get() !== null;

      if (eligible && !isBlocked) {
        if (gifts.length === 0) {
          await post('cart/add.js', { items: [{ id: variantId, quantity: 1 }] });
          cart = await getCart();
          announce('add', [{ id: String(variantId), quantity: 1 }], cart);
        } else if (gifts.length > 1 || gifts[0].quantity !== 1) {
          for (const [index, gift] of gifts.entries()) {
            await post('cart/change.js', { id: gift.key, quantity: index === 0 ? 1 : 0 });
          }
          cart = await getCart();
          announce('update', gifts.map((g, i) => ({ id: g.key, quantity: i === 0 ? 1 : 0 })), cart);
        }

        const gift = cart.items.find(isGift);
        if (gift && gift.final_line_price > 0) {
          storage.set(signature);
          await post('cart/change.js', { id: gift.key, quantity: 0 });
          announce('remove', [{ id: gift.key, quantity: 0 }], await getCart());
        }
      } else if (!eligible && gifts.length) {
        for (const gift of gifts) {
          await post('cart/change.js', { id: gift.key, quantity: 0 });
        }
        announce('remove', gifts.map((g) => ({ id: g.key, quantity: 0 })), await getCart());
      }
    } catch (error) {
      console.warn('[tt-free-gift]', error);
    } finally {
      running = false;
      if (queued) {
        queued = false;
        sync();
      }
    }
  }

  const afterUpdate = (event) => {
    if (dispatching) return;
    Promise.resolve(event.promise)
      .catch(() => {})
      .then(sync);
  };

  document.addEventListener(StandardEvents.cartLinesUpdate, afterUpdate);
  document.addEventListener(StandardEvents.cartDiscountUpdate, afterUpdate);
  window.addEventListener('pageshow', (event) => {
    if (event.persisted) sync();
  });

  sync();
}
